import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  MoreVertical,
  Save,
  X,
  FolderOpen,
  Tag,
  Calendar,
  User,
  Eye,
  EyeOff,
  RefreshCw,
} from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../server";

const TypeManager = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [newType, setNewType] = useState("");

  // Fetch types on component mount
  useEffect(() => {
    fetchTypes();
  }, [showDeleted]);

  const fetchTypes = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/type/all`, {
        params: {
          deleted: showDeleted,
          pageSize: 200,
        },
      });
      setTypes(data.data);
    } catch (error) {
      toast.error("Failed to fetch types");
      console.error("Error fetching types:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddType = async (e) => {
    e.preventDefault();
    if (!newType.trim()) {
      toast.error("Please enter a type name");
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await axios.post(`${server}/type/new`, null, {
        params: {
          type: newType.trim(),
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      toast.success("Type added successfully!");
      setNewType("");
      setShowAddModal(false);
      fetchTypes(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add type");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditType = async (type) => {
    if (!newType.trim()) {
      toast.error("Please enter a type name");
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await axios.put(`${server}/type/updateById`, null, {
        params: {
          typeId: type.id,
          type: newType.trim(),
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (data.success) {
        toast.success("Type updated successfully!");
        setEditingType(null);
        setNewType("");
        fetchTypes(); // Refresh the list
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update type");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTypePermanently = async (typeId) => {
    try {
      setSubmitting(true);
      const { data } = await axios.delete(`${server}/type/deleteForeverById`, {
        params: {
          typeId: typeId,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (data.success) {
        toast.success("Type deleted!");
        fetchTypes(); // Refresh the list
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete type");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteType = async (typeId) => {
    try {
      setSubmitting(true);
      const { data } = await axios.delete(`${server}/type/deleteById`, {
        params: {
          typeId: typeId,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (data.success) {
        toast.success("Type deleted!");
        fetchTypes(); // Refresh the list
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete type");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestoreType = async (typeId) => {
    try {
      setSubmitting(true);
      const { data } = await axios.post(
        `${server}/type/recoverDeletedById`,
        null,
        {
          params: {
            typeId: typeId,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (data.success) {
        toast.success("Type restored successfully!");
        fetchTypes(); // Refresh the list
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to restore type");
    } finally {
      setSubmitting(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: {
        duration: 0.2,
      },
    },
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const startEdit = (type) => {
    setEditingType(type);
    setNewType(type.type);
  };

  const cancelEdit = () => {
    setEditingType(null);
    setNewType("");
  };

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <motion.div
        className="max-w-6xl mx-auto"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Header */}
        <motion.div className="text-center mb-8" variants={itemVariants}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl shadow-lg">
              <FolderOpen className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">Type Manager</h1>
          </div>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Manage your question types. Create, edit, and organize types for
            better content management.
          </p>
        </motion.div>

        {/* Controls */}
        <motion.div
          className="bg-gray-800 rounded-2xl p-6 mb-6 shadow-lg border border-gray-700"
          variants={itemVariants}
        >
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="flex-1 w-full lg:w-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search types..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                />
              </div>
            </div>

            {/* Filters and Actions */}
            <div className="flex flex-wrap gap-3">
              {/* Show Deleted Toggle */}
              <button
                onClick={() => setShowDeleted(!showDeleted)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl transition-all duration-200 ${
                  showDeleted
                    ? "bg-red-500/20 text-red-400 border border-red-500/30"
                    : "bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600"
                }`}
              >
                {showDeleted ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
                {showDeleted ? "Hide Deleted" : "Show Deleted"}
              </button>

              {/* Refresh */}
              <button
                onClick={fetchTypes}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-3 bg-gray-700 text-gray-300 border border-gray-600 rounded-xl hover:bg-gray-600 transition-all duration-200 disabled:opacity-50"
              >
                <RefreshCw
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>

              {/* Add Type */}
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-4 py-3 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-200"
              >
                <Plus className="w-4 h-4" />
                Add Type
              </button>
            </div>
          </div>
        </motion.div>

        {/* Types List */}
        <motion.div variants={itemVariants}>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
          ) : types.length === 0 ? (
            <div className="text-center py-12 bg-gray-800 rounded-2xl border border-gray-700">
              <FolderOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-400 mb-2">
                {showDeleted ? "No deleted types found" : "No types found"}
              </h3>
              <p className="text-gray-500">
                {searchTerm
                  ? "Try adjusting your search terms"
                  : showDeleted
                  ? "Deleted types will appear here"
                  : "Get started by creating your first type"}
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {types.map((type) => (
                <motion.div
                  key={type.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className={`bg-gray-800 rounded-2xl p-6 border transition-all duration-200 ${
                    type.deleted
                      ? "border-red-500/30 bg-red-500/5"
                      : "border-gray-700 hover:border-gray-600"
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Type Info */}
                    <div className="flex-1">
                      {editingType?.id === type.id ? (
                        <div className="flex items-center gap-3">
                          <input
                            type="text"
                            value={newType}
                            onChange={(e) => setNewType(e.target.value)}
                            className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter type name..."
                            autoFocus
                          />
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <h3 className="text-xl font-semibold text-white">
                              {type.type}
                            </h3>
                            {type.deleted && (
                              <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium">
                                Deleted
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                            <div className="flex items-center gap-1">
                              <Tag className="w-4 h-4" />
                              <span>Slug: {type.slug}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(type.createdAt)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              <span>By User {type.createdBy}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {editingType?.id === type.id ? (
                        <>
                          <button
                            onClick={() => handleEditType(type)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                          >
                            <Save className="w-4 h-4" />
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-600 text-gray-300 rounded-lg hover:bg-gray-500 transition-colors disabled:opacity-50"
                          >
                            <X className="w-4 h-4" />
                            Cancel
                          </button>
                        </>
                      ) : type.deleted ? (
                        <>
                          <button
                            onClick={() => handleRestoreType(type.id)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Restore
                          </button>
                          <button
                            onClick={() => handleDeleteTypePermanently(type.id)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Permanently
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEdit(type)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteType(type.id)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Stats */}
        {!loading && types.length > 0 && (
          <motion.div
            className="mt-6 text-center text-gray-400 text-sm"
            variants={itemVariants}
          >
            Showing {types.length} of {types.length} types
            {showDeleted && " (including deleted)"}
          </motion.div>
        )}
      </motion.div>

      {/* Add Type Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="fixed inset-0 bg-slate-950 bg-opacity-90 rounded-3xl flex justify-center p-4 z-50"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="bg-gray-800 rounded-2xl h-fit p-6 max-w-md w-full border border-gray-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white">
                  Add New Type
                </h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleAddType} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Type Name
                  </label>
                  <input
                    type="text"
                    value={newType}
                    onChange={(e) => setNewType(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    placeholder="Enter type name..."
                    autoFocus
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-3 bg-gray-700 text-gray-300 rounded-xl hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-200 disabled:opacity-50"
                  >
                    {submitting ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    {submitting ? "Adding..." : "Add Type"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TypeManager;
